import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import React, { useEffect, useRef, useState } from 'react'
import { EditView } from './components/EditView'
import { StaticView } from './components/StaticView'
import { API_PROFILE } from '../../../../constants'
import { Company } from '../../../../model/company.model'
import { RequestMethod, UserAccountType } from '../../../../model/enums/enums'
import { Apprentice, Person } from '../../../../model/person.model'
import { State } from '../../../../model/state.model'
import { apprenticeUpdated } from '../../../../redux/basic/apprentice/apprenticeReducer'
import { employeeUpdated } from '../../../../redux/basic/employee/employeeReducer'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { getUserBigAvatarSrc } from '../../../../util/getAvatar'
import { showCheckMarkToast } from '../../../util/checkmark-toast/CheckmarkToast'
import { PhotoEditDialog } from '../../../util/dialog/PhotoEditDialog'
import { LoadingSpinner } from '../../../util/loading-spinner/LoadingSpinner'

export const Profile = () => {
  const [inEditMode, setInEditMode] = useState<boolean>(false)
  const [hasLoaded, setHasLoaded] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { personId } = useAppSelector((state) => state.user)

  const dispatcher = useAppDispatch()

  // its either an apprentice or an employee
  const thePerson = useAppSelector((state) =>
    [...state.apprentices, ...state.employees].find(
      (pers: Apprentice | Person) => pers.person_id === personId
    )
  )

  const companies = useAppSelector((state) => state.companies)

  const [company, setCompany] = useState<Company>()

  const [showProfilePictureEditor, setShowProfilePictureEditor] =
    useState<boolean>(false)

  const toast = useRef<Toast>(null)

  const hideEditProfilePictureDialog = () => {
    setShowProfilePictureEditor(false)
  }

  const loadProfileData = async () => {
    setIsLoading(true)
    // load additional profile information
    await handleRequest<Person>(API_PROFILE(), RequestMethod.GET).then(
      (response: State<Person>) => {
        if (!response?.data || !thePerson) {
          setIsLoading(false)
          console.warn(
            '[Profile] retrieving additional profile information was not successful.'
          )
          return
        }
        if (thePerson.user_account_type === UserAccountType.Apprentice) {
          dispatcher(apprenticeUpdated(response.data))
          setIsLoading(false)
          return
        }
        dispatcher(employeeUpdated(response.data))
        setIsLoading(false)
      }
    )
    setHasLoaded(true)
  }

  useEffect(() => {
    // company is already set
    if (company) {
      return
    }
    if (thePerson?.id_company && companies.length > 0) {
      const comp = companies.find((c) => c.company_id == thePerson.id_company)
      setCompany(comp)
    }
  }, [thePerson?.id_company, companies])

  useEffect(() => {
    if (!isLoading && !hasLoaded && thePerson?.user_account_type && personId) {
      loadProfileData()
    }
  }, [personId, thePerson?.user_account_type])

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Button
            type={'button'}
            icon={inEditMode ? 'fa-solid fa-times' : 'fa-solid fa-pen'}
            className="float-right p-button-rounded p-button-primary p-button-text mr-1"
            onClick={() => setInEditMode(!inEditMode)}
          />
          <div className="col-12" style={{ textAlign: 'center' }}>
            <div className={'image-container'}>
              <Avatar
                image={getUserBigAvatarSrc(thePerson)}
                className={'person-image ml-4'}
                shape={'circle'}
                size={'large'}
                onClick={() => setShowProfilePictureEditor(true)}
              />
              <div
                className="image-overlay"
                onClick={() => setShowProfilePictureEditor(true)}
              >
                <i className="icon-blue person-image-icon fas fa-camera fa-3x ml-4 mb-2" />
              </div>
            </div>
          </div>
          {inEditMode ? (
            <EditView
              company={company}
              person={thePerson}
              onCancelButtonClick={() => setInEditMode(false)}
              onSaveButtonClick={() => {
                setInEditMode(false)
                showCheckMarkToast(toast)
              }}
            />
          ) : (
            <StaticView
              company={company}
              person={thePerson}
              onEditButtonClick={() => setInEditMode(true)}
            />
          )}
          <PhotoEditDialog
            hideEditProfilePictureDialog={hideEditProfilePictureDialog}
            person={thePerson}
            showProfilePictureEditor={showProfilePictureEditor}
            is_self={true}
          />
          <Toast ref={toast} position={'bottom-right'} />
        </>
      )}
    </>
  )
}